import React from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Pencil, Trash2, UserRoundCog, ArrowRight } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import SubstitutionDialog from './SubstitutionDialog';
import { useAuth } from '../../context/authContext';
import { useToast } from '../ui/use-toast';
import {
    createSubstitution,
    updateSubstitution,
    deleteSubstitution,
} from '../../services/substitution.service';

export default function SubstitutionsManager({ substitutions, users, onChanged }) {

    const { user } = useAuth();
    const { toast } = useToast();

    const existingUserIds = substitutions.map((s) => s.user?.userId).filter(Boolean);

    const handleCreate = async (data) => {
        const response = await createSubstitution(user.token, user.organisationId, data);
        if (response && response.status === 201) {
            toast({ title: 'Success', description: 'Substitution created', variant: 'success' });
            onChanged && onChanged();
            return true;
        }
        const message = response?.data?.error?.message || 'There was an error creating the substitution';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return false;
    };

    const handleUpdate = async (substitutionId, data) => {
        const response = await updateSubstitution(user.token, user.organisationId, substitutionId, data);
        if (response && response.status === 200) {
            toast({ title: 'Success', description: 'Substitution updated', variant: 'success' });
            onChanged && onChanged();
            return true;
        }
        const message = response?.data?.error?.message || 'There was an error updating the substitution';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return false;
    };

    const handleDelete = async (substitutionId) => {
        const response = await deleteSubstitution(user.token, user.organisationId, substitutionId);
        if (response && response.status === 200) {
            toast({ title: 'Success', description: 'Substitution removed', variant: 'success' });
            onChanged && onChanged();
        } else {
            const message = response?.data?.error?.message || 'There was an error removing the substitution';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-row items-start justify-between gap-4">
                    <div className="flex flex-col gap-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <UserRoundCog className="w-5 h-5 text-yavrio-blue shrink-0" />
                            <h3 className="my-0">Substitutions</h3>
                        </div>
                        <p className="text-sm text-gray-500 mb-0">
                            Assign a stand-in approver when a user cannot fulfil a workflow step (e.g. they submitted the payment or are inactive).
                        </p>
                    </div>
                    <SubstitutionDialog
                        users={users}
                        existingUserIds={existingUserIds}
                        onSave={handleCreate}
                    >
                        <Button className="yvr-button gap-2 shrink-0">
                            <Plus className="w-4 h-4" /> New Substitution
                        </Button>
                    </SubstitutionDialog>
                </div>
            </CardHeader>
            <CardContent>
                {substitutions.length === 0 ? (
                    <p className="text-sm text-gray-500 mb-0">No substitutions yet.</p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-white">
                                <TableHead>User</TableHead>
                                <TableHead>Substitute</TableHead>
                                <TableHead></TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {substitutions.map((substitution) => (
                                <TableRow key={substitution.id} className="hover:bg-white">
                                    <TableCell>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium">{substitution.user?.name}</span>
                                            <span className="text-xs text-gray-500">{substitution.user?.email}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium">{substitution.substitute?.name}</span>
                                                <span className="text-xs text-gray-500">{substitution.substitute?.email}</span>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <SubstitutionDialog
                                            substitution={substitution}
                                            users={users}
                                            onSave={(data) => handleUpdate(substitution.id, data)}
                                        >
                                            <Button variant="ghost" className="p-2 text-yavrio-blue">
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                        </SubstitutionDialog>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <ConfirmDialog
                                            title="Remove Substitution"
                                            description={
                                                <>
                                                    {`Remove the substitute for `}
                                                    <b>{substitution.user?.name}</b>
                                                    {`?`}
                                                </>
                                            }
                                            cancel="Cancel"
                                            action="Remove"
                                            variant="destructive"
                                            handleAction={() => handleDelete(substitution.id)}
                                        >
                                            <Button variant="ghost" className="p-2 text-red-400">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </ConfirmDialog>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
}
